// components
import type { BoxProps } from '@mui/material';
import { styled, Box } from '@mui/material';

// enums
import { ToolbarFlowEnum } from '../ToolbarWrapper.enums';

// types
import type { ToolbarFlowType } from '../ToolbarSet.types';

export const StyledToolbarContainer = styled(Box, {
  shouldForwardProp: (prop: string) =>
    ![
      'isRightPositioned',
      'isZenPositioned',
      'zenOpen',
      'showDateNavigation',
      'toolbarFlow'
    ].includes(prop)
})<
  BoxProps & {
    isRightPositioned?: boolean;
    isZenPositioned?: boolean;
    zenOpen?: boolean;
    showDateNavigation?: boolean;
    toolbarFlow?: ToolbarFlowType;
  }
>(({
  theme,
  isRightPositioned,
  isZenPositioned,
  zenOpen,
  showDateNavigation = true,
  toolbarFlow = ToolbarFlowEnum.GRID
}) => {
  const isPositioned = isRightPositioned || isZenPositioned;
  const isFlex = toolbarFlow === ToolbarFlowEnum.FLEX;
  const base = {
    display: isFlex ? 'flex' : isPositioned ? 'flex' : 'grid',
    flexDirection: isPositioned ? 'column' : 'row',
    // The middle column hugs the date navigation rather than taking a third of the width, so anything
    // placed either side of it sits right beside it instead of across a gap of empty column. The two
    // side columns are minmax(0, 1fr) rather than a bare 1fr: 1fr is minmax(auto, 1fr), so a wide left
    // slot (the 550px plan unit selector) would push its min-content floor past its share and knock
    // the picker off-centre. With a 0 floor the two columns stay exactly equal and the picker stays
    // centred, and a toolbar without injected components looks identical either way.
    gridTemplateColumns: showDateNavigation
      ? 'minmax(0, 1fr) auto minmax(0, 1fr)'
      : 'minmax(0, 1fr) minmax(0, 1fr)',
    zIndex: isZenPositioned ? 1000 : 1,
    border: `1px solid ${theme.vars.palette.outline.variant}`,
    borderRadius: theme.spacing(4),
    padding: theme.spacing(1, isPositioned ? 1 : 3),
    backgroundColor: theme.palette.common.white,
    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.04)',
    height: 'fit-content',
    alignItems: 'center',
    flexWrap: 'wrap',
    gridTemplateAreas: showDateNavigation
      ? `'leftContent dateNavigation rightContent'`
      : `'leftContent rightContent'`,

    '& > :nth-of-type(1)': {
      gridArea: 'leftContent'
    },
    // Without the date navigation the toolbar has two children, so the right section is the second
    // one — it has to claim rightContent itself or it lands in a grid area that no longer exists and
    // renders mid-toolbar instead of at the right edge.
    '& > :nth-of-type(2)': {
      gridArea: showDateNavigation ? 'dateNavigation' : 'rightContent'
    },
    '& > :nth-of-type(3)': {
      gridArea: 'rightContent'
    },

    // Narrow screens give the date navigation a row of its own above the two sections. With no date
    // navigation the base two-column layout already is that row, so there is nothing to override.
    [theme.breakpoints.down('medium')]: showDateNavigation
      ? {
          gridTemplateColumns: '1fr 1fr',
          gridTemplateAreas: `
      'dateNavigation dateNavigation'
      'leftContent rightContent'
    `
        }
      : {}
  } as const;

  if (!isZenPositioned) {
    return {
      position: 'relative',
      ...base
    };
  }

  return {
    position: 'absolute',
    right: 0,
    ...base,
    transform: zenOpen ? 'translateX(0)' : 'translateX(calc(100% + 16px))',
    transition: 'transform 220ms ease',
    pointerEvents: zenOpen ? 'auto' : 'none'
  };
});

/**
 * One of the toolbar's three grid areas. The container assigns its areas by child position
 * (nth-of-type 1/2/3), so the injected beforeDatePicker/afterDatePicker components have to sit
 * *inside* the left and right areas rather than become extra children — otherwise five children
 * compete for three areas and the date picker loses its middle column.
 *
 * Both sections pack from the start. That puts a component injected on the left at the toolbar's left
 * edge, where the activity filter belongs, and it is also why one injected on the right still lands
 * beside the date navigation: there the slot is the first child and the ActionStrip pins itself to the
 * far edge on its own, with marginLeft: auto.
 *
 * Not space-between: with a left slot that pushes it away from the edge and into the middle.
 */
export const StyledToolbarSection = styled(Box, {
  shouldForwardProp: (prop: string) => prop !== 'isVertical'
})<BoxProps & { isVertical?: boolean }>(({ theme, isVertical }) => ({
  display: 'flex',
  // The section has to grow so the right ActionStrip's marginLeft: auto has free space to absorb. Only
  // the row flows need it: in vertical flow StyledActionStripBox already sets marginLeft: 0, and a
  // flex-basis of 0 in a column container would make both sections as tall as the taller one. flexGrow
  // rather than flex: 1 so content stays the floor — the 550px plan unit selector must not be squeezed
  // to a 50% share. In GRID flow the section is a grid item and this is ignored either way.
  ...(isVertical ? {} : { flexGrow: 1 }),
  flexDirection: isVertical ? 'column' : 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: theme.spacing(2),
  minWidth: 0
}));

export const StyledActionStripBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'justifyEnd' && prop !== 'isVertical'
})<BoxProps & { justifyEnd?: boolean; isVertical?: boolean }>(
  ({ theme, justifyEnd, isVertical }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    ...(justifyEnd ? { marginLeft: isVertical ? 0 : 'auto' } : {}),
    ...(isVertical ? { flexDirection: 'column' } : {})
  })
);
